import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Handshake, ArrowRight, CheckCircle2, Copy, Info, Building2, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const PARTNERSHIP_TYPES = [
  ["funding", "Programme funding"],
  ["programme_sponsorship", "Programme sponsorship"],
  ["community_project", "Community project"],
  ["research", "Research"],
  ["technical_assistance", "Technical assistance"],
  ["csr", "Corporate social responsibility"],
  ["university", "University partnership"],
  ["training", "Training"],
  ["equipment", "Equipment donation"],
  ["volunteer", "Volunteer programme"],
  ["humanitarian", "Humanitarian assistance"],
  ["joint_funding", "Joint funding application"],
  ["other", "Other"],
];

const PARTNER_KINDS = [
  { icon: Building2, t: "International charities & NGOs" },
  { icon: Globe, t: "Foundations & development agencies" },
  { icon: Users, t: "Universities & research institutions" },
  { icon: Handshake, t: "Corporate CSR & social enterprises" },
];

const AREAS = [
  "Programme funding", "Community project funding", "Technical partnership", "Research partnership",
  "Academic collaboration", "Corporate social responsibility", "Equipment & in-kind support", "Volunteer & professional support",
];

const EMPTY = {
  organisation_name: "", organisation_type: "", registration_number: "", year_established: "",
  contact_person: "", position: "", email: "", phone: "", whatsapp: "",
  website: "", office_address: "", country: "United Kingdom",
  primary_area_of_work: "",
  proposed_intervention: "",
  organisation_description: "",
  amount_requested: 0,
  total_project_cost: 0,
  expected_impact: "",
  partnership_types: [],
};

export default function International() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { document.title = "International Donations & Partnerships | Dutsen Kauna Foundation"; }, []);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleType = (v) => setForm((p) => ({
    ...p, partnership_types: p.partnership_types.includes(v)
      ? p.partnership_types.filter((x) => x !== v)
      : [...p.partnership_types, v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organisation_name || !form.email || !form.phone || !form.proposed_intervention) {
      toast.error("Please complete required fields."); return;
    }
    setLoading(true);
    try {
      // Uses the Applications backend with primary_category=partnership → DKF-PAR serial
      const payload = {
        applicant_type: "organisation",
        primary_category: "partnership",
        help_needs: form.partnership_types,
        organisation_name: form.organisation_name,
        organisation_type: form.organisation_type,
        registration_number: form.registration_number,
        year_established: form.year_established,
        contact_person: form.contact_person,
        position: form.position,
        website: form.website,
        office_address: form.office_address,
        state: form.country,
        primary_area_of_work: form.primary_area_of_work,
        phone: form.phone,
        whatsapp: form.whatsapp,
        email: form.email,
        organisation_description: form.organisation_description,
        proposed_intervention: form.proposed_intervention,
        beneficiaries_description: "",
        problem_description: form.proposed_intervention,
        support_description: form.proposed_intervention,
        amount_requested: Number(form.amount_requested) || 0,
        total_project_cost: Number(form.total_project_cost) || 0,
        expected_impact: form.expected_impact,
        consent_true: true,
        consent_verification: true,
        consent_processing: true,
      };
      const { data } = await api.post("/applications", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="partnership-success" className="bg-[#f8f4e8] py-16 min-h-[70vh]">
        <div className="dk-container max-w-2xl">
          <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
            <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={48} />
            <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-2">Partnership proposal received</h1>
            <p className="text-[#0a0d12]/75 mb-6">Thank you. Our partnerships team will review and get back to you shortly.</p>
            <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">Partnership reference</div>
              <div className="font-mono text-2xl md:text-3xl font-bold text-[#d6aa43]" data-testid="partnership-serial">{result.serial}</div>
              <button onClick={() => navigator.clipboard.writeText(result.serial).then(() => toast.success("Copied"))} className="mt-3 text-xs text-white inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to={`/apply/track?serial=${encodeURIComponent(result.serial)}`} className="dk-btn-primary">Track partnership request</Link>
              <Link to="/" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Return home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="international-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjaGFyaXR5JTIwaGFuZHMlMjB0b2dldGhlcnxlbnwwfHx8fDE3ODI2MDk1NjJ8MA&ixlib=rb-4.1.0&q=85" alt="International partners meeting Foundation staff and community leaders" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">International donations & partnerships</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-4xl leading-[1.05]">
            Partner with us to transform lives and <span className="italic text-[#d6aa43]">strengthen communities.</span>
          </h1>
          <p className="mt-6 text-white text-lg max-w-2xl">
            Diaspora members, international charities, businesses, universities, foundations and philanthropic donors —
            you can help us deliver practical, sustainable impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/donate" className="dk-btn-primary" data-testid="intl-donate-hero">
              Donate internationally <ArrowRight size={16} />
            </Link>
            <a href="#partner-form" className="dk-btn-outline" data-testid="intl-partner-hero">Become a partner</a>
          </div>
          <div className="mt-6 text-sm text-white">
            Built on <strong className="text-white">Transparency · Independence · Accountability · Community Ownership · Measurable Impact.</strong>
          </div>
        </div>
      </section>

      {/* Support anywhere */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <div className="max-w-3xl mb-10">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Support from anywhere</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Choose where your contribution goes.</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              "Individual empowerment", "Women & youth empowerment", "Family food support", "Education",
              "Sponsor a Child", "Rural electrification", "Solar-energy projects", "Water & sanitation",
              "Agriculture & food security", "Community infrastructure", "Emergency relief", "Skills development",
              "Research & needs assessments", "Health & wellbeing", "Where most needed",
            ].map((t) => (
              <div key={t} className="p-4 rounded-md bg-[#f8f4e8] border border-black/5 text-sm font-semibold text-[#0a0d12]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to give */}
      <section className="py-20 bg-[#155e3f] text-white">
        <div className="dk-container">
          <div className="max-w-3xl mb-10">
            <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">How you can give</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3">Six ways to make a difference.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "One-time donation", d: "A single contribution to a programme or general activities." },
              { t: "Monthly giving", d: "Become a recurring supporter and help us plan sustainable interventions." },
              { t: "Sponsor a programme", d: "Support a specific programme or defined group of beneficiaries." },
              { t: "Fund a community project", d: "Provide full or partial funding for an identified intervention." },
              { t: "Corporate giving", d: "Companies contribute through charitable giving, social-impact and approved partnerships." },
              { t: "Institutional funding", d: "Foundations, NGOs and development organisations provide project, programme or research funding." },
            ].map((c, i) => (
              <div key={c.t} className="p-6 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur">
                <div className="text-[#d6aa43] font-bold text-xs tracking-[0.2em]">0{i + 1}</div>
                <h3 className="font-serif text-xl font-bold mt-2 mb-2">{c.t}</h3>
                <p className="text-sm text-white leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Currencies + trust */}
      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Currencies supported</span>
            <h2 className="font-serif text-3xl text-[#0a0d12] font-bold mt-3 mb-5">Donate in your preferred currency.</h2>
            <div className="flex flex-wrap gap-2">
              {[
                ["GBP", "£", "British Pounds"],
                ["USD", "$", "US Dollars"],
                ["EUR", "€", "Euros"],
                ["NGN", "₦", "Nigerian Naira"],
              ].map(([c, s, n]) => (
                <div key={c} className="p-4 rounded-xl bg-white border border-black/5 flex items-center gap-3 min-w-[140px]">
                  <div className="w-10 h-10 rounded-full bg-[#155e3f] text-[#d6aa43] flex items-center justify-center font-serif text-lg font-bold">{s}</div>
                  <div>
                    <div className="font-bold text-[#0a0d12]">{c}</div>
                    <div className="text-xs text-[#0a0d12]/60">{n}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-[#0a0d12]/70">
              You'll always see the donation amount, selected currency, applicable equivalent, processing charges (where relevant) and the final amount before payment.
            </p>
            <Link to="/donate" className="dk-btn-primary mt-6" data-testid="intl-donate-cta">Start a donation <ArrowRight size={14} /></Link>
          </div>
          <div className="p-6 rounded-2xl bg-[#155e3f] text-white">
            <Info className="text-[#d6aa43] mb-3" />
            <h3 className="font-serif text-xl font-bold mb-3">Restricted vs unrestricted</h3>
            <div className="space-y-3 text-sm text-white">
              <p><strong className="text-white">Restricted:</strong> you specify an approved programme or project. Funds are used for that designated purpose, subject to agreed terms.</p>
              <p><strong className="text-white">Unrestricted:</strong> the Foundation directs your contribution to the area of greatest legitimate need.</p>
              <p className="pt-3 border-t border-white/10 text-xs">Donors clearly see which type of donation they are making before payment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner kinds */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <div className="max-w-3xl mb-10">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Who we partner with</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Organisations that share our commitment.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PARTNER_KINDS.map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={k.t} className="p-6 rounded-xl border border-black/5 bg-[#f8f4e8] hover:-translate-y-1 transition">
                  <div className="w-11 h-11 rounded-full bg-[#155e3f] text-[#d6aa43] flex items-center justify-center mb-3"><Icon size={20} /></div>
                  <p className="text-[#0a0d12] font-semibold">{k.t}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {AREAS.map((a) => (<div key={a} className="p-4 rounded-md bg-white border border-black/5 text-sm text-[#0a0d12]">{a}</div>))}
          </div>
        </div>
      </section>

      {/* Partnership form */}
      <section id="partner-form" className="py-20 bg-[#f8f4e8]">
        <div className="dk-container max-w-3xl">
          <div className="mb-8">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Partnership application</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Become an international partner.</h2>
            <p className="mt-3 text-[#0a0d12]/70">Submitting this proposal will generate your unique <strong>DKF-PAR-2026-XXXXXX</strong> reference number for tracking.</p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl p-8 md:p-10 border border-black/5" data-testid="partnership-form">
            <h3 className="font-serif text-xl text-[#0a0d12] font-bold mb-4">Organisation</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Organisation name" required v={form.organisation_name} on={(v) => up("organisation_name", v)} tid="par-org-name" />
              <F label="Organisation type" v={form.organisation_type} on={(v) => up("organisation_type", v)} placeholder="Charity / NGO / Company / University" />
              <F label="Country" v={form.country} on={(v) => up("country", v)} tid="par-country" />
              <F label="Website" v={form.website} on={(v) => up("website", v)} />
              <F label="Registration number" v={form.registration_number} on={(v) => up("registration_number", v)} />
              <F label="Year established" v={form.year_established} on={(v) => up("year_established", v)} />
              <F label="Primary area of work" v={form.primary_area_of_work} on={(v) => up("primary_area_of_work", v)} className="sm:col-span-2" />
              <F label="Office address" v={form.office_address} on={(v) => up("office_address", v)} className="sm:col-span-2" />
            </div>

            <h3 className="font-serif text-xl text-[#0a0d12] font-bold mt-8 mb-4">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Contact person" v={form.contact_person} on={(v) => up("contact_person", v)} />
              <F label="Position" v={form.position} on={(v) => up("position", v)} />
              <F label="Email" type="email" required v={form.email} on={(v) => up("email", v)} tid="par-email" />
              <F label="Phone" required v={form.phone} on={(v) => up("phone", v)} tid="par-phone" />
              <F label="WhatsApp" v={form.whatsapp} on={(v) => up("whatsapp", v)} className="sm:col-span-2" />
            </div>

            <h3 className="font-serif text-xl text-[#0a0d12] font-bold mt-8 mb-4">Proposed partnership</h3>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2 block">Partnership type (select all that apply)</label>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="par-types">
                {PARTNERSHIP_TYPES.map(([k, l]) => (
                  <label key={k} className={`p-2.5 rounded-md border-2 text-xs cursor-pointer flex items-center gap-2 ${form.partnership_types.includes(k) ? "border-[#d6aa43] bg-[#f8f4e8]" : "border-black/10 hover:border-[#d6aa43]"}`}>
                    <input type="checkbox" checked={form.partnership_types.includes(k)} onChange={() => toggleType(k)} data-testid={`par-type-${k}`} />
                    {l}
                  </label>
                ))}
              </div>
            </div>

            <TA label="Tell us about your organisation" v={form.organisation_description} on={(v) => up("organisation_description", v)} />
            <TA label="What would you like to support / propose?" required v={form.proposed_intervention} on={(v) => up("proposed_intervention", v)} tid="par-proposal" />

            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Estimated financial contribution" type="number" v={form.amount_requested} on={(v) => up("amount_requested", v)} />
              <F label="Total project cost (if known)" type="number" v={form.total_project_cost} on={(v) => up("total_project_cost", v)} />
            </div>
            <TA label="Expected outcome / impact" v={form.expected_impact} on={(v) => up("expected_impact", v)} />

            <div className="mt-6 p-4 rounded-md bg-[#f8f4e8] border border-[#d6aa43]/20 text-sm text-[#0a0d12]/80 flex gap-2">
              <Info size={16} className="text-[#d6aa43] flex-shrink-0 mt-0.5" />
              Substantial partnerships are subject to due diligence, risk & compliance review, and a formal MoU before implementation.
              The Foundation reserves the right to decline donations or partnerships that could compromise its independence.
            </div>

            <button type="submit" disabled={loading} className="dk-btn-primary mt-6 w-full justify-center disabled:opacity-60" data-testid="partner-submit">
              {loading ? "Submitting…" : "Submit partnership proposal"}
            </button>
          </form>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">How partnerships are assessed</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 mb-8">Every substantial partnership passes through 13 steps.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[
              "Initial review", "Alignment assessment", "Due diligence", "Project assessment",
              "Budget review", "Risk & compliance review", "Foundation approval", "Partnership agreement / MoU",
              "Funding / resource transfer", "Implementation", "Monitoring", "Reporting", "Impact evaluation",
            ].map((t, i) => (
              <div key={t} className="p-4 rounded-md border border-white/10 bg-white/[0.03] flex gap-3">
                <div className="text-[#d6aa43] font-serif text-lg font-bold">0{(i + 1).toString().padStart(2, "0")}</div>
                <div className="text-sm text-white font-semibold pt-1">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function F({ label, v, on, type = "text", required, className = "", tid, placeholder }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="w-full px-4 py-2.5 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={tid} />
    </div>
  );
}
function TA({ label, v, on, required, tid }) {
  return (
    <div className="mb-4">
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block mt-4">{label}{required ? " *" : ""}</label>
      <textarea required={required} rows={4} value={v ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={tid} />
    </div>
  );
}
